import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SocketService } from '../../game/services/SocketService';
import { HttpService } from '../../game/services/HttpService';
import { ServerMessages } from '../../game/constants/ServerMessages';
import { UserMessages } from '../../game/constants/UserMessages';
import { MessageHandler } from '../../game/services/SocketService';
import './FriendsChat.component.css';

export interface OnlineFriend {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  online: boolean;
}

export interface ChatMessage {
  id?: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
  isMe: boolean;
}

interface FriendChatMessageDto {
  id: string;
  senderId: string;
  senderName: string;
  recipientId: string;
  text: string;
  createdAt: number;
}

function formatMessageTime(epochMs: number): string {
  const d = new Date(epochMs);
  const now = new Date();
  const sameDay = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export interface OpenChat {
  friendId: string;
  friendName: string;
  minimized: boolean;
  messages: ChatMessage[];
  hasMoreOlder: boolean;
  loadingOlder: boolean;
}

export interface FriendsChatProps {
  onClose: () => void;
  socketRef: React.RefObject<SocketService | null>;
  isConnected: boolean;
  /** When provided, use parent's unread state (so counts persist when modal is closed) */
  unreadByFriendId?: Record<string, number>;
  setUnreadByFriendId?: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  /** Called when total unread count changes (e.g. for dashboard badge); optional if parent derives from unreadByFriendId */
  onUnreadChange?: (total: number) => void;
  /** When set, open chat for this friend (e.g. from Friends modal "Message" button); cleared via onOpenChatHandled */
  openChatForFriend?: { id: string; name: string } | null;
  onOpenChatHandled?: () => void;
  /** Called when the focused chat or panel minimized state changes (so parent can show badge excluding focused) */
  onPanelStateChange?: (focusedFriendId: string | null, panelMinimized: boolean) => void;
}

const FriendsChat: React.FC<FriendsChatProps> = ({ onClose, socketRef, isConnected, unreadByFriendId: propsUnreadByFriendId, setUnreadByFriendId: propsSetUnreadByFriendId, onUnreadChange, openChatForFriend, onOpenChatHandled, onPanelStateChange }) => {
  const [onlineFriends, setOnlineFriends] = useState<OnlineFriend[]>([]);
  const [openChats, setOpenChats] = useState<OpenChat[]>([]);
  const [internalUnreadByFriendId, setInternalUnreadByFriendId] = useState<Record<string, number>>({});
  const unreadByFriendId = propsUnreadByFriendId ?? internalUnreadByFriendId;
  const setUnreadByFriendId = propsSetUnreadByFriendId ?? setInternalUnreadByFriendId;
  const isControlledUnread = propsUnreadByFriendId != null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [friendsListFolded, setFriendsListFolded] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches
  );
  const [onlineSectionFolded, setOnlineSectionFolded] = useState(false);
  const [offlineSectionFolded, setOfflineSectionFolded] = useState(false);
  const handlersRef = useRef<{ online: MessageHandler; chat: MessageHandler } | null>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const loadedInitialForRef = useRef<Set<string>>(new Set());
  const httpServiceRef = useRef<HttpService | null>(null);
  const expandedFriendIdRef = useRef<string | null>(null);
  const panelMinimizedRef = useRef(false);
  const markSeenOnServerRef = useRef<(friendId: string) => void>(() => { });

  const MESSAGE_PAGE_SIZE = 50;

  const getFriendDisplayName = (f: OnlineFriend) =>
    `${f.firstName || ''} ${f.lastName || ''}`.trim() || f.email || f.id;

  const requestOnlineFriends = () => {
    const socket = socketRef.current;
    if (!socket?.isConnected()) {
      setError('Dashboard not connected');
      return;
    }
    setError(null);
    setLoading(true);
    socket.sendMessage({ type: UserMessages.GET_ONLINE_FRIENDS });
  };

  useEffect(() => {
    const http = new HttpService();
    const token = localStorage.getItem('token');
    if (token) http.setToken(token);
    httpServiceRef.current = http;
    return () => { httpServiceRef.current = null; };
  }, []);

  const markSeenOnServer = useCallback((friendId: string) => {
    const socket = socketRef.current;
    if (!socket?.isConnected()) return;
    socket.sendMessage({ type: UserMessages.MARK_FRIEND_CHAT_SEEN, friendId });
  }, [socketRef]);
  markSeenOnServerRef.current = markSeenOnServer;

  useEffect(() => {
    if (!isConnected) {
      setOnlineFriends([]);
      return;
    }
    requestOnlineFriends();
  }, [isConnected]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleOnlineFriends: MessageHandler = (message: any) => {
      setLoading(false);
      const friends = message.friends;
      if (Array.isArray(friends)) {
        setOnlineFriends(friends);
      }
    };

    const handleFriendChat: MessageHandler = (message: any) => {
      const senderId = message.senderId as string;
      const senderName = (message.senderName as string) || 'Unknown';
      const text = (message.text as string) || '';
      const timestamp = typeof message.timestamp === 'number' ? message.timestamp : Date.now();
      const id = typeof message.id === 'string' && message.id ? message.id : undefined;
      const userId = localStorage.getItem('userId') || '';
      const isMe = senderId === userId;

      /* Only add unread when chat is not open or not focused on this friend (dashboard badge and in-chat badge) */
      const chatFocusedOnSender = !panelMinimizedRef.current && expandedFriendIdRef.current === senderId;
      if (!isMe && !chatFocusedOnSender) {
        setUnreadByFriendId((prev) => ({ ...prev, [senderId]: (prev[senderId] || 0) + 1 }));
      }
      if (!isMe && chatFocusedOnSender) {
        markSeenOnServerRef.current(senderId);
      }

      setOpenChats((prev) => {
        const existing = prev.find((c) => c.friendId === senderId);
        const newMsg: ChatMessage = { id, senderId, senderName, text, timestamp, isMe };
        if (existing) {
          return prev.map((c) => {
            if (c.friendId !== senderId) return c;
            const merged = [...c.messages, newMsg].sort((a, b) => a.timestamp - b.timestamp);
            return { ...c, messages: merged };
          });
        }
        return [
          ...prev.map((c) => ({ ...c, minimized: true })),
          {
            friendId: senderId,
            friendName: senderName,
            minimized: true,
            messages: [newMsg],
            hasMoreOlder: false,
            loadingOlder: false,
          },
        ];
      });
      if (!isMe && expandedFriendIdRef.current === senderId) {
        setTimeout(() => messageEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    };

    socket.onMessage(ServerMessages.ONLINE_FRIENDS, handleOnlineFriends);
    socket.onMessage(ServerMessages.FRIEND_CHAT_MESSAGE, handleFriendChat);
    handlersRef.current = { online: handleOnlineFriends, chat: handleFriendChat };

    if (socket.isConnected()) {
      setLoading(true);
      socket.sendMessage({ type: UserMessages.GET_ONLINE_FRIENDS });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.offMessage(ServerMessages.ONLINE_FRIENDS, handleOnlineFriends);
        socketRef.current.offMessage(ServerMessages.FRIEND_CHAT_MESSAGE, handleFriendChat);
      }
      handlersRef.current = null;
    };
  }, []);

  const dtoToChatMessage = useCallback((d: FriendChatMessageDto, userId: string): ChatMessage => ({
    id: d.id,
    senderId: d.senderId,
    senderName: d.senderName || 'Unknown',
    text: d.text,
    timestamp: d.createdAt,
    isMe: d.senderId === userId,
  }), []);

  const mergeAndSortMessages = useCallback((a: ChatMessage[], b: ChatMessage[]): ChatMessage[] => {
    const byKey = (m: ChatMessage) => m.id ?? `${m.senderId}|${m.text}|${m.timestamp}`;
    const seen = new Set<string>();
    const combined: ChatMessage[] = [];
    for (const m of [...a, ...b]) {
      const key = byKey(m);
      if (seen.has(key)) continue;
      seen.add(key);
      combined.push(m);
    }
    combined.sort((x, y) => x.timestamp - y.timestamp);
    return combined;
  }, []);

  const loadInitialHistoryForFriend = useCallback(async (friendId: string) => {
    if (loadedInitialForRef.current.has(friendId)) return;
    const http = httpServiceRef.current;
    if (!http) return;
    loadedInitialForRef.current.add(friendId);
    try {
      const res = await http.get<{ status?: string; data?: FriendChatMessageDto[] }>(
        `friend-chat/messages?friendId=${encodeURIComponent(friendId)}&limit=${MESSAGE_PAGE_SIZE}`
      );
      const list = Array.isArray(res?.data) ? res.data : [];
      const userId = localStorage.getItem('userId') || '';
      const history: ChatMessage[] = list.map((d) => dtoToChatMessage(d, userId));
      const hasMoreOlder = list.length === MESSAGE_PAGE_SIZE;
      setOpenChats((prev) => {
        const chat = prev.find((c) => c.friendId === friendId);
        if (!chat) return prev;
        const merged = mergeAndSortMessages(chat.messages, history);
        return prev.map((c) =>
          c.friendId === friendId
            ? { ...c, messages: merged, hasMoreOlder, loadingOlder: false }
            : c
        );
      });
      /* Scroll to bottom to show latest message when chat is first opened and history has loaded */
      setTimeout(() => messageEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      loadedInitialForRef.current.delete(friendId);
      console.warn('Failed to load friend chat history:', e);
    }
  }, [dtoToChatMessage, mergeAndSortMessages]);

  const loadOlderMessagesForFriend = useCallback(async (friendId: string, beforeTimestamp: number) => {
    const http = httpServiceRef.current;
    if (!http) return;
    const scrollEl = messagesScrollRef.current;
    const prevScrollHeight = scrollEl?.scrollHeight ?? 0;
    const prevScrollTop = scrollEl?.scrollTop ?? 0;
    setOpenChats((prev) => {
      const chat = prev.find((c) => c.friendId === friendId);
      if (!chat || chat.loadingOlder || !chat.hasMoreOlder) return prev;
      return prev.map((c) => (c.friendId === friendId ? { ...c, loadingOlder: true } : c));
    });
    try {
      const res = await http.get<{ status?: string; data?: FriendChatMessageDto[] }>(
        `friend-chat/messages?friendId=${encodeURIComponent(friendId)}&limit=${MESSAGE_PAGE_SIZE}&before=${beforeTimestamp}`
      );
      const list = Array.isArray(res?.data) ? res.data : [];
      const userId = localStorage.getItem('userId') || '';
      const older: ChatMessage[] = list.map((d) => dtoToChatMessage(d, userId));
      const hasMoreOlder = list.length === MESSAGE_PAGE_SIZE;
      setOpenChats((prev) => {
        const c = prev.find((x) => x.friendId === friendId);
        if (!c) return prev;
        const merged = mergeAndSortMessages(older, c.messages);
        return prev.map((x) =>
          x.friendId === friendId ? { ...x, messages: merged, hasMoreOlder, loadingOlder: false } : x
        );
      });
      requestAnimationFrame(() => {
        if (scrollEl && older.length > 0) {
          const newScrollHeight = scrollEl.scrollHeight;
          scrollEl.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        }
      });
    } catch (e) {
      console.warn('Failed to load older messages:', e);
      setOpenChats((prev) => prev.map((c) => (c.friendId === friendId ? { ...c, loadingOlder: false } : c)));
    }
  }, [dtoToChatMessage, mergeAndSortMessages]);

  const openChat = (friend: OnlineFriend) => {
    const name = getFriendDisplayName(friend);
    setUnreadByFriendId((prev) => ({ ...prev, [friend.id]: 0 }));
    markSeenOnServer(friend.id);
    setOpenChats((prev) => {
      const exists = prev.find((c) => c.friendId === friend.id);
      const next = exists
        ? prev.map((c) => ({
          ...c,
          minimized: c.friendId !== friend.id,
        }))
        : [
          ...prev.map((c) => ({ ...c, minimized: true })),
          { friendId: friend.id, friendName: name, minimized: false, messages: [], hasMoreOlder: false, loadingOlder: false },
        ];
      if (!exists) return next;
      return next.map((c) =>
        c.friendId === friend.id ? { ...c, minimized: false, friendName: name } : c
      );
    });
    loadInitialHistoryForFriend(friend.id);
  };

  const openChatByIdAndName = useCallback((friendId: string, friendName: string) => {
    setUnreadByFriendId((prev) => ({ ...prev, [friendId]: 0 }));
    markSeenOnServer(friendId);
    setOpenChats((prev) => {
      const exists = prev.find((c) => c.friendId === friendId);
      const next = exists
        ? prev.map((c) => ({
          ...c,
          minimized: c.friendId !== friendId,
        }))
        : [
          ...prev.map((c) => ({ ...c, minimized: true })),
          { friendId, friendName, minimized: false, messages: [], hasMoreOlder: false, loadingOlder: false },
        ];
      if (!exists) return next;
      return next.map((c) =>
        c.friendId === friendId ? { ...c, minimized: false, friendName } : c
      );
    });
    loadInitialHistoryForFriend(friendId);
  }, [loadInitialHistoryForFriend, setUnreadByFriendId, markSeenOnServer]);

  useEffect(() => {
    if (openChatForFriend?.id && openChatForFriend?.name) {
      openChatByIdAndName(openChatForFriend.id, openChatForFriend.name);
      onOpenChatHandled?.();
    }
  }, [openChatForFriend, openChatByIdAndName, onOpenChatHandled]);

  const minimizeChat = (friendId: string) => {
    setOpenChats((prev) =>
      prev.map((c) => (c.friendId === friendId ? { ...c, minimized: true } : c))
    );
  };

  const closeChat = (friendId: string) => {
    setOpenChats((prev) => {
      const next = prev.filter((c) => c.friendId !== friendId);
      const closedWasActive = prev.some((c) => c.friendId === friendId && !c.minimized);
      if (closedWasActive && next.length > 0) {
        return next.map((c, i) => ({ ...c, minimized: i !== 0 }));
      }
      return next;
    });
  };

  const focusChat = (friendId: string) => {
    setUnreadByFriendId((prev) => ({ ...prev, [friendId]: 0 }));
    markSeenOnServer(friendId);
    setOpenChats((prev) =>
      prev.map((c) => ({ ...c, minimized: c.friendId !== friendId }))
    );
    loadInitialHistoryForFriend(friendId);
  };

  const sendMessage = (friendId: string, text: string) => {
    const t = text.trim();
    if (!t) return;
    const socket = socketRef.current;
    if (!socket?.isConnected()) {
      setError('Not connected');
      return;
    }
    socket.sendMessage({
      type: UserMessages.SEND_FRIEND_CHAT,
      recipientId: friendId,
      text: t,
    });
    const userId = localStorage.getItem('userId') || '';
    const username = localStorage.getItem('username') || 'You';
    setOpenChats((prev) =>
      prev.map((c) => {
        if (c.friendId !== friendId) return c;
        const newMsg: ChatMessage = { senderId: userId, senderName: username, text: t, timestamp: Date.now(), isMe: true };
        const merged = [...c.messages, newMsg].sort((a, b) => a.timestamp - b.timestamp);
        return { ...c, messages: merged };
      })
    );
    inputRefs.current[friendId] = null;
    setTimeout(() => messageEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const expandedChat = openChats.find((c) => !c.minimized);
  const minimizedChats = openChats.filter((c) => c.minimized);

  expandedFriendIdRef.current = expandedChat?.friendId ?? null;
  panelMinimizedRef.current = panelMinimized;

  /* Keep focused chat's unread at 0 so badges never show for the open chat */
  useEffect(() => {
    if (expandedChat?.friendId) {
      setUnreadByFriendId((prev) =>
        (prev[expandedChat.friendId] === 0 ? prev : { ...prev, [expandedChat.friendId]: 0 })
      );
    }
  }, [expandedChat?.friendId, setUnreadByFriendId]);

  /* Report panel state so parent can show badge excluding focused friend when panel is open */
  useEffect(() => {
    onPanelStateChange?.(expandedChat?.friendId ?? null, panelMinimized);
  }, [expandedChat?.friendId, panelMinimized, onPanelStateChange]);

  /* Scroll to bottom when user opens or switches to a chat so the latest message is visible */
  useEffect(() => {
    if (expandedChat?.friendId) {
      const t = setTimeout(() => messageEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      return () => clearTimeout(t);
    }
  }, [expandedChat?.friendId]);

  const totalUnread = Object.values(unreadByFriendId).reduce((a, b) => a + b, 0);
  /* When panel is open and a chat is focused, don't count that friend in display total (for minimized-arrow badge) */
  const displayTotalUnread =
    !panelMinimized && expandedChat
      ? totalUnread - (unreadByFriendId[expandedChat.friendId] ?? 0)
      : totalUnread;

  useEffect(() => {
    onUnreadChange?.(totalUnread);
  }, [totalUnread, onUnreadChange]);

  return (
    <div className={`friends-chat-overlay ${panelMinimized ? 'is-minimized' : ''}`}>
      <button
        type="button"
        className="friends-chat-minimize-arrow"
        onClick={() => setPanelMinimized(false)}
        title="Open Friends Chat"
        aria-label="Open Friends Chat"
      >
        <span className="friends-chat-arrow-icon">💬</span>
        {displayTotalUnread > 0 && (
          <span className="friends-chat-arrow-unread">{displayTotalUnread > 99 ? '99+' : displayTotalUnread}</span>
        )}
      </button>
      <div className="friends-chat-panel">
        <div className="friends-chat-header">
          <h2>Friends Chat</h2>
          <div className="friends-chat-header-actions">
            <button type="button" className="friends-chat-minimize-panel" onClick={() => setPanelMinimized(true)} title="Minimize to arrow">
              ▼
            </button>
            <button type="button" className="friends-chat-refresh" onClick={requestOnlineFriends} disabled={loading || !isConnected} title="Refresh">
              {loading ? '…' : '↻'}
            </button>
            <button type="button" className="friends-chat-close" onClick={onClose}>×</button>
          </div>
        </div>
        {!isConnected && (
          <div className="friends-chat-status friends-chat-status-offline">Dashboard disconnected. Reconnect to see online friends.</div>
        )}
        {error && <div className="friends-chat-error">{error}</div>}
        <div className={`friends-chat-body ${friendsListFolded ? 'friends-list-folded' : ''}`}>
          <div className="friends-chat-friends-toggle-wrap">
            <button
              type="button"
              className="friends-chat-friends-toggle"
              onClick={() => setFriendsListFolded((v) => !v)}
              title={friendsListFolded ? 'Show friends list' : 'Hide friends list'}
              aria-expanded={!friendsListFolded}
            >
              {friendsListFolded ? '▶ Friends' : '◀ Friends'}
            </button>
          </div>
          <div className="friends-chat-online">
            <button
              type="button"
              className={`friends-chat-section-header ${onlineSectionFolded ? 'folded' : ''}`}
              onClick={() => setOnlineSectionFolded((v) => !v)}
              aria-expanded={!onlineSectionFolded}
            >

              <span>Online <span className="friends-chat-online-dot" /> <span className="friends-chat-section-chevron">{onlineSectionFolded ? '▶' : '▼'}</span></span>

            </button>
            {!onlineSectionFolded && (
              onlineFriends.filter((f) => f.online).length === 0 ? (
                <p className="friends-chat-empty">No friends online</p>
              ) : (
                <div className="friends-chat-online-list">
                  {onlineFriends.filter((f) => f.online).map((friend) => {
                    const isActiveChat = expandedChat?.friendId === friend.id;
                    const showUnread = unreadByFriendId[friend.id] > 0 && !isActiveChat;
                    return (
                      <div key={friend.id} className="friends-chat-friend-row">
                        <div
                          role="button"
                          tabIndex={0}
                          className="friends-chat-online-name"
                          onClick={() => openChat(friend)}
                          onKeyDown={(e) => e.key === 'Enter' && openChat(friend)}
                        >
                          {showUnread && (
                            <span className="friends-chat-unread-dot" title="New message(s)" aria-hidden />
                          )}
                          {getFriendDisplayName(friend)}
                          {showUnread && (
                            <span className="friends-chat-unread-badge" title={`${unreadByFriendId[friend.id]} unread message(s)`}>
                              {unreadByFriendId[friend.id] > 99 ? '99+' : unreadByFriendId[friend.id]}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
            <button
              type="button"
              className={`friends-chat-section-header friends-chat-offline-header ${offlineSectionFolded ? 'folded' : ''}`}
              onClick={() => setOfflineSectionFolded((v) => !v)}
              aria-expanded={!offlineSectionFolded}
            >
              <span>Offline <span className="friends-chat-section-chevron">{offlineSectionFolded ? '▶' : '▼'}</span></span>

            </button>
            {!offlineSectionFolded && (
              onlineFriends.filter((f) => !f.online).length === 0 ? (
                <p className="friends-chat-empty">No friends offline</p>
              ) : (
                <div className="friends-chat-online-list friends-chat-offline-list">
                  {onlineFriends.filter((f) => !f.online).map((friend) => {
                    const isActiveChat = expandedChat?.friendId === friend.id;
                    const showUnread = unreadByFriendId[friend.id] > 0 && !isActiveChat;
                    return (
                      <div key={friend.id} className="friends-chat-friend-row">
                        {showUnread ? (
                          <span className="friends-chat-unread-dot offline" title="New message(s)" aria-hidden />
                        ) : (
                          <span className="friends-chat-offline-placeholder" aria-hidden />
                        )}
                        <div
                          role="button"
                          tabIndex={0}
                          className="friends-chat-online-name offline"
                          onClick={() => openChat(friend)}
                          onKeyDown={(e) => e.key === 'Enter' && openChat(friend)}
                        >
                          {getFriendDisplayName(friend)}
                          {showUnread && (
                            <span className="friends-chat-unread-badge" title={`${unreadByFriendId[friend.id]} unread message(s)`}>
                              {unreadByFriendId[friend.id] > 99 ? '99+' : unreadByFriendId[friend.id]}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
          <div className="friends-chat-windows">
            {openChats.length > 0 && (
              <>
                <div className="friends-chat-tabs">
                  {openChats.map((chat) => (
                    <div
                      key={chat.friendId}
                      className={`friends-chat-tab ${!chat.minimized ? 'active' : ''}`}
                      onClick={() => chat.minimized && focusChat(chat.friendId)}
                      role="tab"
                      tabIndex={chat.minimized ? 0 : -1}
                      onKeyDown={(e) => e.key === 'Enter' && chat.minimized && focusChat(chat.friendId)}
                    >
                      <span className="friends-chat-tab-label" title={chat.friendName}>
                        {chat.friendName.length > 12 ? chat.friendName.slice(0, 10) + '…' : chat.friendName}
                      </span>
                      {unreadByFriendId[chat.friendId] > 0 && chat.minimized && (
                        <span className="friends-chat-tab-unread">{unreadByFriendId[chat.friendId] > 99 ? '99+' : unreadByFriendId[chat.friendId]}</span>
                      )}
                      <button
                        type="button"
                        className="friends-chat-tab-close"
                        onClick={(e) => { e.stopPropagation(); closeChat(chat.friendId); }}
                        title="Close"
                        aria-label="Close chat"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                {expandedChat && (
                  <div className="friends-chat-window expanded">
                    <div className="friends-chat-window-header">
                      <span>{expandedChat.friendName}</span>
                      <button type="button" className="friends-chat-win-btn" onClick={() => closeChat(expandedChat.friendId)} title="Close">×</button>
                    </div>
                    <div
                      className="friends-chat-messages"
                      ref={messagesScrollRef}
                      onScroll={() => {
                        const el = messagesScrollRef.current;
                        const chat = expandedChat;
                        if (!el || !chat || (chat.hasMoreOlder ?? false) === false || (chat.loadingOlder ?? false)) return;
                        if (chat.messages.length === 0) return;
                        if (el.scrollTop <= 80) {
                          loadOlderMessagesForFriend(chat.friendId, chat.messages[0].timestamp);
                        }
                      }}
                    >
                      {(expandedChat.loadingOlder ?? false) && (
                        <div className="friends-chat-loading-older">Loading older messages…</div>
                      )}
                      {expandedChat.messages.map((msg, i) => (
                        <div key={msg.id ?? `msg-${i}`} className={`friends-chat-msg ${msg.isMe ? 'me' : ''}`}>
                          {!msg.isMe && <span className="friends-chat-msg-sender">{msg.senderName}</span>}
                          <span className="friends-chat-msg-text">{msg.text}</span>
                          <span className="friends-chat-msg-time">{formatMessageTime(msg.timestamp)}</span>
                        </div>
                      ))}
                      <div ref={messageEndRef} />
                    </div>
                    <form
                      className="friends-chat-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const input = inputRefs.current[expandedChat.friendId] || (e.currentTarget.querySelector('input') as HTMLInputElement);
                        if (input) {
                          sendMessage(expandedChat.friendId, input.value);
                          input.value = '';
                        }
                      }}
                    >
                      <input
                        ref={(el) => { inputRefs.current[expandedChat.friendId] = el; }}
                        type="text"
                        placeholder="Type a message..."
                        maxLength={500}
                        autoComplete="off"
                      />
                      <button type="submit">Send</button>
                    </form>
                  </div>
                )}
              </>
            )}
            {openChats.length === 0 && (
              <div className="friends-chat-no-chat">
                <p>Open a chat from the friends list</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FriendsChat;
